import { useCallback } from 'react';
import { Modals, useModalMethods } from 'src/store/modals';
import { SnackbarOptions } from 'src/store/snackbar';
import { useNotifications } from './use-notifications';

type Options = { successMessage?: string; alertOptions?: Omit<SnackbarOptions, 'severity'> };

type ClipboardValue = string | { plainText: string; html?: string | null };

export const useCopyToClipboard = (options?: Options) => {
  const { successMessage, alertOptions } = options || {};
  const { showSuccessAlert } = useNotifications();
  const { showModal } = useModalMethods(Modals.CopyText);

  const copyToClipboard = useCallback(
    async (value: ClipboardValue, options?: { openInModal?: boolean }) => {
      const plainText =
        typeof value === 'string' ? value : value.plainText || value.html || '';
      const html = typeof value === 'string' ? undefined : value.html || undefined;

      if (options?.openInModal) {
        showModal({
          text: plainText,
          onSuccess: () => showSuccessAlert(successMessage ?? 'Copied!', alertOptions),
        });
        return;
      }

      if (html) {
        const item = new ClipboardItem({
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        });
        await navigator.clipboard.write([item]);
        showSuccessAlert(successMessage ?? 'Copied!', alertOptions);
        return;
      }

      await navigator.clipboard.writeText(plainText);
      showSuccessAlert(successMessage ?? 'Copied!', alertOptions);
    },
    [alertOptions, showSuccessAlert, successMessage, showModal],
  );

  /**
   * Copies a friendly link (Markdown and HTML format) to the clipboard.
   * @param label - The text label for the link.
   * @param url - The URL to be linked.
   */
  const copyFriendlyLink = useCallback(
    async (label: string, url: string) => {
      const markdownLink = `[${label}](${url})`;
      const htmlLink = `<a href="${url}">${label}</a>`;

      const item = new ClipboardItem({
        'text/html': new Blob([htmlLink], { type: 'text/html' }),
        'text/plain': new Blob([markdownLink], { type: 'text/plain' }),
      });

      await navigator.clipboard.write([item]);
      showSuccessAlert(successMessage ?? 'Copied!', alertOptions);
    },
    [alertOptions, showSuccessAlert, successMessage],
  );

  return {
    copyToClipboard,
    copyFriendlyLink,
  };
};
